/**
 * CustomerProfilePage — Volledige klant profile view voor admin.
 * Toont: bedrijfsdetails, billing, payments, invoices, integrations, AI, audit.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft, Building2, CreditCard, FileText, Link2, Sparkles,
  Mail, Crown, CheckCircle, AlertCircle, Clock, Activity,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtEur = (n) => `€ ${Number(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

const PLAN_BADGE = {
  free_scan: { label: 'Free Scan', color: '#64748b', bg: '#f1f5f9' },
  self_service_connected_14_95: { label: '€14,95 Self-Service', color: '#0f172a', bg: '#fef3c7' },
  managed_ai_launch_month_995_95: { label: '€995,95 Launch', color: '#D4A017', bg: '#fffbeb' },
  managed_ai_recurring_2495_95: { label: '€2.495,95 Recurring', color: '#7c3aed', bg: '#f5f3ff' },
};

export default function CustomerProfilePage() {
  const { id } = useParams();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  useEffect(() => {
    api('get', `/api/admin/customers/${id}/profile`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Profile laden mislukt'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-20 text-[#94a3b8]">Profile laden...</div>;
  if (!data || data.error) return <div className="text-center py-20 text-red-600">{data?.error || 'Niet gevonden'}</div>;

  const { user, company, billing, payments = [], invoices = [], integrations = [], orders = [], contracts = [], ai = {}, audit = [], lead_origin, scan, opdrachten } = data;
  const plan = PLAN_BADGE[billing?.plan_state] || PLAN_BADGE.free_scan;

  const openInvoice = async (invId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/${invId}/html`;
    const token = localStorage.getItem('portal_token');
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const html = await r.text();
    const w = window.open('', '_blank');
    w.document.write(html); w.document.close();
  };

  const resendInvite = async () => {
    if (!window.confirm('Verstuur de welkomstmail opnieuw?')) return;
    try {
      const r = await api('post', `/api/admin/clients/${user.id}/resend-invite`);
      toast.success(`Email status: ${r.data.email_status}`);
    } catch (e) { toast.error('Mislukt'); }
  };

  return (
    <div data-testid="customer-profile-page">
      <Link to="/admin/clients" className="text-xs text-[#94a3b8] hover:text-[#0f172a] flex items-center gap-1.5 mb-3">
        <ArrowLeft size={12} /> Terug naar klanten
      </Link>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span style={{ background: plan.bg, color: plan.color }} className="text-[10px] font-bold px-2 py-1 rounded uppercase">{plan.label}</span>
              {user.disabled && <span className="bg-red-100 text-red-700 text-[10px] font-bold px-2 py-1 rounded uppercase">Gedeactiveerd</span>}
              {!user.password_hash && <span className="bg-amber-100 text-amber-700 text-[10px] font-bold px-2 py-1 rounded uppercase">Setup hangt</span>}
              {lead_origin && <span className="bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-1 rounded uppercase">Lead-conversion</span>}
            </div>
            <h1 className="text-2xl font-extrabold text-[#0f172a] flex items-center gap-2">
              <Building2 size={20} className="text-[#D4A017]" />
              {company?.name || user?.name || user?.email}
            </h1>
            <p className="text-sm text-[#64748b] mt-1">{user?.email} {user?.name && ` · ${user.name}`}</p>
            {company?.phone && <p className="text-xs text-[#64748b]">{company.phone}</p>}
            {company?.website && <a href={company.website} target="_blank" rel="noreferrer" className="text-xs text-[#D4A017] underline">{company.website}</a>}
          </div>
          <div className="text-right">
            <p className="text-[10px] text-[#94a3b8] uppercase font-bold">MRR contributie</p>
            <p className="text-2xl font-extrabold text-[#0f172a]">{fmtEur(billing?.current_amount_ex_vat || 0)}</p>
            <p className="text-xs text-[#64748b]">excl. btw / mnd</p>
            {billing?.billing_phase === 'launch_month' && billing?.days_until_recurring !== null && (
              <p className="text-[11px] text-[#D4A017] font-bold mt-1">Nog {billing.days_until_recurring}d in launch maand</p>
            )}
          </div>
        </div>
        <div className="flex gap-2 mt-4 flex-wrap">
          {!user.password_hash && (
            <button onClick={resendInvite} data-testid="resend-invite-btn" className="text-xs bg-[#D4A017] hover:bg-[#b88a13] text-[#0f172a] font-bold px-3 py-2 rounded-lg">
              Verstuur uitnodiging opnieuw
            </button>
          )}
          <Link to={`/admin/managed?user_id=${user.id}`} className="text-xs bg-white border border-slate-200 text-[#0f172a] font-bold px-3 py-2 rounded-lg">Managed workflows</Link>
          <Link to="/admin/invoices" className="text-xs bg-white border border-slate-200 text-[#0f172a] font-bold px-3 py-2 rounded-lg">Alle facturen</Link>
        </div>
      </div>

      {/* Snapshot stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <SnapStat label="Betalingen" value={payments.length} icon={CreditCard} />
        <SnapStat label="Facturen" value={invoices.length} icon={FileText} />
        <SnapStat label="Integraties" value={integrations.filter(i => i.status === 'connected').length} icon={Link2} />
        <SnapStat label="AI acties" value={ai?.total || 0} icon={Sparkles} highlight={ai?.pending_approval > 0} />
        <SnapStat label="Opdrachten" value={`${opdrachten?.completed || 0}/${opdrachten?.total || 0}`} icon={CheckCircle} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {[
          ['overview', 'Overzicht', Activity],
          ['billing', `Billing (${payments.length})`, CreditCard],
          ['invoices', `Facturen (${invoices.length})`, FileText],
          ['integrations', `Integraties (${integrations.length})`, Link2],
          ['audit', `Audit (${audit.length})`, Clock],
        ].map(([id, label, Icon]) => (
          <button
            key={id}
            data-testid={`tab-${id}`}
            onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-xs font-bold border-b-2 -mb-px flex items-center gap-1.5 whitespace-nowrap ${tab === id ? 'border-[#D4A017] text-[#0f172a]' : 'border-transparent text-[#94a3b8] hover:text-[#0f172a]'}`}
          >
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        {tab === 'overview' && (
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] text-[#94a3b8] uppercase font-bold mb-2">Bedrijf</p>
                <Detail label="Naam" value={company?.name} />
                <Detail label="Branche" value={company?.industry || company?.branche} />
                <Detail label="Locatie" value={company?.city || company?.locatie} />
                <Detail label="KvK" value={company?.kvk_number || company?.kvk} />
                <Detail label="BTW-nr" value={company?.vat_number || company?.btw_nummer} />
              </div>
              <div>
                <p className="text-[10px] text-[#94a3b8] uppercase font-bold mb-2">Billing</p>
                <Detail label="Plan" value={billing?.plan_label} />
                <Detail label="Fase" value={billing?.billing_phase} />
                <Detail label="Huidig ex. btw" value={fmtEur(billing?.current_amount_ex_vat || 0)} />
                {billing?.billing_phase === 'launch_month' && <Detail label="Volgend bedrag" value={`${fmtEur(billing?.next_amount_ex_vat || 0)} excl. btw`} />}
                <Detail label="Aangemaakt" value={fmtDate(user.created_at)} />
              </div>
            </div>
            {lead_origin && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-[10px] text-purple-700 uppercase font-bold">Lead-origine</p>
                <p className="text-sm text-[#0f172a] mt-1">Source: <b>{lead_origin.source || 'unknown'}</b> · Aangemaakt: {fmtDate(lead_origin.created_at)} · Geconverteerd: {fmtDate(lead_origin.converted_at)}</p>
              </div>
            )}
          </div>
        )}

        {tab === 'billing' && (
          <div className="divide-y divide-slate-100">
            {payments.length === 0 ? <p className="text-sm text-[#94a3b8] text-center py-6">Geen betalingen.</p>
              : payments.map(p => (
                <div key={p.id} className="py-3 flex justify-between items-center gap-3">
                  <div>
                    <p className="text-sm font-bold text-[#0f172a]">{p.name}</p>
                    <p className="text-xs text-[#64748b]">{fmtDate(p.created_at)} · {p.package_id || p.service_id || ''}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{fmtEur(p.amount)}</p>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${p.payment_status === 'paid' ? 'bg-green-100 text-green-700' : p.payment_status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{p.payment_status}</span>
                  </div>
                </div>
              ))}
          </div>
        )}

        {tab === 'invoices' && (
          <div className="divide-y divide-slate-100">
            {invoices.length === 0 ? <p className="text-sm text-[#94a3b8] text-center py-6">Nog geen facturen.</p>
              : invoices.map(inv => (
                <div key={inv.id} className="py-3 flex justify-between items-center gap-3">
                  <div>
                    <p className="text-sm font-bold text-[#0f172a]">{inv.invoice_number}</p>
                    <p className="text-xs text-[#64748b]">{(inv.issue_date || '').slice(0, 10)} · {inv.lines?.[0]?.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{fmtEur(inv.total_incl_vat)}</p>
                    <p className="text-[10px] text-[#94a3b8]">ex: {fmtEur(inv.subtotal_ex_vat)}</p>
                  </div>
                  <button data-testid={`view-inv-${inv.id}`} onClick={() => openInvoice(inv.id)} className="text-xs bg-white border border-slate-200 hover:border-[#D4A017] text-[#0f172a] font-bold px-3 py-1.5 rounded">Bekijk</button>
                </div>
              ))}
          </div>
        )}

        {tab === 'integrations' && (
          <div className="divide-y divide-slate-100">
            {integrations.length === 0 ? <p className="text-sm text-[#94a3b8] text-center py-6">Nog geen gekoppelde accounts.</p>
              : integrations.map(i => (
                <div key={i.id || i.provider} className="py-3 flex justify-between items-center gap-3">
                  <div>
                    <p className="text-sm font-bold text-[#0f172a] capitalize">{i.provider}</p>
                    <p className="text-xs text-[#64748b]">{i.account_name || i.account_id || ''}</p>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${i.status === 'connected' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-[#64748b]'}`}>{i.status}</span>
                </div>
              ))}
          </div>
        )}

        {tab === 'audit' && (
          <div className="divide-y divide-slate-100">
            {audit.length === 0 ? <p className="text-sm text-[#94a3b8] text-center py-6">Nog geen audit events.</p>
              : audit.map(a => (
                <div key={a.id || a.timestamp} className="py-2.5 flex gap-3 items-start">
                  <Clock size={11} className="text-[#94a3b8] mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs"><b className="text-[#0f172a]">{a.action}</b> <span className="text-[#94a3b8]">door {a.actor || a.user_id?.slice(0, 8) || 'system'}</span></p>
                    {a.details && <p className="text-[11px] text-[#64748b] mt-0.5">{a.details}</p>}
                    <p className="text-[10px] text-[#94a3b8] mt-0.5">{fmtDate(a.timestamp)}</p>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

const SnapStat = ({ label, value, icon: Icon, highlight }) => (
  <div className={`bg-white border rounded-xl p-3 ${highlight ? 'border-[#D4A017]' : 'border-slate-200'}`}>
    <div className="flex justify-between items-center">
      <p className="text-2xl font-extrabold text-[#0f172a]">{value}</p>
      <Icon size={16} className={highlight ? 'text-[#D4A017]' : 'text-[#94a3b8]'} />
    </div>
    <p className="text-[10px] text-[#94a3b8] uppercase font-bold tracking-wide mt-1">{label}</p>
  </div>
);

const Detail = ({ label, value }) => (
  <div className="flex justify-between py-1.5 text-sm border-b border-slate-100 last:border-0">
    <span className="text-[#64748b]">{label}</span>
    <span className="font-medium text-[#0f172a] text-right truncate ml-2 max-w-[200px]">{value || '—'}</span>
  </div>
);
